import { useEffect } from "react";
import { isAxiosError } from "axios";
import type { FallbackProps } from "react-error-boundary";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router-dom";

import api from "@/api/api";
import HeaderAction from "@/components/header-action";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/auth-context";
import RefreshCcw01 from "@/icons/Line/RefreshCw01";
import { isDev } from "@/utils/env";

export const FallbackComponent = ({
  error,
  resetErrorBoundary,
}: FallbackProps) => {
  const errorMessage =
    error instanceof Error ? error.message : "Erreur inconnue";
  const errorStack = error instanceof Error ? error.stack : undefined;

  return (
    <div className="p-10 flex flex-col gap-8 self-start" role="alert">
      <HeaderAction title="Une erreur inatendue est survenue." />
      <div className="flex flex-col gap-4 items-start">
        {isDev && (
          <div className="text-destructive">
            <pre>{errorMessage}</pre>
            {errorStack ? <pre>{errorStack}</pre> : null}
          </div>
        )}
        <Button onClick={resetErrorBoundary}>
          <RefreshCcw01 width={20} height={20} className="mr-2" /> Réessayer
        </Button>
      </div>
    </div>
  );
};

const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  useEffect(() => {
    const interceptorId = api.interceptors.response.use(undefined, (error) => {
      if (!isAxiosError(error)) throw error;
      if (error.response?.status === 401) {
        alert("Vous avez été déconnecté");
        logout();
        navigate("/connexion");
      } else throw error;
    });
    return () => {
      api.interceptors.response.eject(interceptorId);
    };
  }, [navigate, logout]);

  return (
    <ReactErrorBoundary FallbackComponent={FallbackComponent}>
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
